import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { shopCartItemsEqual } from '../helpers/shopCartHelpers'
import { clearShopcartFromStorage } from './helpers'
import { ShopCart, UserInstitution, UserType } from './types'
import { TeacherAccountState } from '../firebase/types-teacher'
import { RootState } from './store'
import { User } from 'firebase/auth'
import { ActiveOrg } from './orgSlice'
import { getUserNames } from '../helpers/utils'

interface IUserSlice {
    accountState?: TeacherAccountState
    shopCart: ShopCart[]
    dbStock: { [stripeId: string]: { count: number } }

    auth:
        | {
              loading: false
              status: User | null
          }
        | {
              loading: true
              status: undefined
          }

    user:
        | {
              loading: false
              data: UserType | null
          }
        | {
              loading: true
              data: undefined
          }
    sessionLogout?: boolean
    homeState?: string
}

export const initialState: IUserSlice = {
    auth: {
        loading: true,
        status: undefined,
    },
    user: {
        loading: true,
        data: undefined,
    },

    shopCart: [],
    dbStock: {},
}

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setAuth: (
            state,
            action: PayloadAction<Partial<User> | null | undefined>
        ) => {
            if (action.payload === undefined) {
                Object.assign(state.auth, {
                    loading: true,
                    status: undefined,
                })
            } else {
                Object.assign(state.auth, {
                    loading: false,
                    status: action.payload,
                })
            }
        },

        setEmailVerified: (state, action: PayloadAction<boolean>) => {
            if (state.auth.status) {
                state.auth.status.emailVerified = action.payload
            }
        },

        setUser: (
            state,
            action: PayloadAction<UserType | null | undefined>
        ) => {
            if (action.payload === undefined) {
                Object.assign(state.user, {
                    loading: true,
                    data: undefined,
                })
            } else {
                Object.assign(state.user, {
                    loading: false,
                    data: action.payload,
                })
            }
        },
        setUserHomeState: (state, action: PayloadAction<string>) => {
            state.homeState = action.payload
        },
        setUserLogout: (state) => {
            state.accountState = undefined
            state.dbStock = {}
            state.auth = {
                loading: false,
                status: null,
            }
            state.user = {
                loading: false,
                data: null,
            }
            state.sessionLogout = undefined
        },
        setAccountState: (
            state,
            action: PayloadAction<TeacherAccountState | undefined>
        ) => {
            state.accountState =
                action.payload || TeacherAccountState.INCOMPLETE
        },
        setSessionLogout: (state, action: PayloadAction<boolean>) => {
            state.sessionLogout = action.payload
        },

        addToShopCart: (state, action: PayloadAction<ShopCart>) => {
            const existedItem = state.shopCart.find((item) =>
                shopCartItemsEqual(item, action.payload)
            )

            if (existedItem) {
                existedItem.orderCount =
                    existedItem.orderCount + action.payload.orderCount
                state.shopCart = [...state.shopCart]
            } else {
                state.shopCart.push(action.payload)
                state.shopCart = [...state.shopCart]
            }
        },
        deleteFromShopCart: (state, action: PayloadAction<ShopCart>) => {
            state.shopCart = state.shopCart.filter(
                (item) => !shopCartItemsEqual(item, action.payload)
            )
        },
        clearShopCart: (state) => {
            state.shopCart = []
            clearShopcartFromStorage()
        },
        cacheDbStock: (
            state,
            action: PayloadAction<{
                stripeId: string
                orderCount: number
            }>
        ) => {
            if (
                typeof state.dbStock[action.payload.stripeId] === 'number' &&
                state.dbStock[action.payload.stripeId].count ===
                    action.payload.orderCount
            )
                return
            state.dbStock = {
                ...state.dbStock,
                [action.payload.stripeId]: {
                    count: action.payload.orderCount,
                },
            }
        },
    },
})

export const {
    setUser,
    setAuth,
    setEmailVerified,
    setUserHomeState,
    setUserLogout,
    setAccountState,
    addToShopCart,
    deleteFromShopCart,
    cacheDbStock,
    clearShopCart,
    setSessionLogout,
} = userSlice.actions

export const selectUserAuth = (state: RootState) => state.user.auth
export const selectUserAuthCheck = (state: RootState) => {
    if (state.user.auth.loading) return undefined
    return state.user.auth.status !== null
}

export const selectUserAuthEmailVerifiedCheck = (state: RootState) => {
    if (state.user.auth.loading) return undefined
    return state.user.auth.status?.emailVerified
}

export const selectUserUid = (state: RootState) => {
    if (state.user.auth.loading) return undefined
    return state.user.auth.status?.uid || null
}
export const selectUserData = (state: RootState) => state.user.user
export const selectUserDataCheck = (state: RootState) => {
    if (state.user.user.loading) return undefined
    return state.user.user.data !== null
}
export const selectUserAccountTypeCheck = (state: RootState) => {
    if (state.user.user.loading) return undefined
    return state.user.user.data?.accountType || null
}

export const selectUserName = (state: RootState) =>
    `${state.user.user.data?.firstName} ${state.user.user.data?.lastName}`

const selectUserLoading = (state: RootState) => state.user.user.loading
const selectUserFirstName = (state: RootState) =>
    state.user.user.data?.firstName
const selectUserLastName = (state: RootState) => state.user.user.data?.lastName
const selectUserFirstNameActual = (state: RootState) =>
    state.user.user.data?.firstNameActual

export const selectUserNames = createSelector(
    [
        selectUserLoading,
        selectUserFirstName,
        selectUserLastName,
        selectUserFirstNameActual,
    ],
    (loading, firstName, lastName, firstNameActual) =>
        loading
            ? undefined
            : {
                  firstName: firstName || '',
                  lastName: lastName || '',
                  firstNameActual: firstNameActual,
              }
)

export const selectUserNames_v2 = createSelector(
    [
        selectUserLoading,
        selectUserFirstName,
        selectUserLastName,
        selectUserFirstNameActual,
    ],
    (loading, firstName, lastName, firstNameActual) =>
        loading ? undefined : getUserNames(firstName, lastName, firstNameActual)
)

export const userSchoolsCountSelector = (state: RootState) =>
    state.user.user.loading
        ? undefined
        : !!state.user.user.data?.schools
          ? Object.keys(state.user.user.data.schools).length
          : 0
export const userCentresCountSelector = (state: RootState) =>
    !!state.user.user.loading
        ? undefined
        : !!state.user.user.data?.centres
          ? Object.keys(state.user.user.data.centres).length
          : 0

export const selectUserSchools = (state: RootState) =>
    state.user.user.data?.schools
export const selectUserCentres = (state: RootState) =>
    state.user.user.data?.centres
// export const makeSelectUserSchools = () =>
//     createSelector([selectUserSchools], (schools) => schools)

export const selectSchoolOptions = (state: RootState) => {
    if (state.user.user.loading) return undefined
    if (!state.user.user.data?.accountType) return {}
    return {
        ...(['school', 'organisation'].includes(
            state.user.user.data?.accountType
        )
            ? Object.entries(state.user.user.data?.schools || {}).reduce(
                  (a, c) => ({
                      ...a,
                      [c[0]]: { ...c[1], type: 'school' as 'school' },
                  }),
                  {} as Record<string, UserInstitution>
              )
            : {}),
        ...(['childcareCentre', 'organisation'].includes(
            state.user.user.data?.accountType
        )
            ? Object.entries(state.user.user.data?.centres || {}).reduce(
                  (a, c) => ({
                      ...a,
                      [c[0]]: {
                          ...c[1],
                          type: 'childcareCentre' as 'childcareCentre',
                      },
                  }),
                  {} as Record<string, UserInstitution>
              )
            : {}),
    }
}

export const makeSelectOrgType = () =>
    createSelector(
        [selectInitialOrgId, selectSchoolOptions, selectUserAccountTypeCheck],
        (initialId, options, accountType): ActiveOrg | null | undefined => {
            // Data is loading
            if (
                initialId === undefined ||
                options === undefined ||
                accountType === undefined
            )
                return undefined
            return typeof initialId !== 'string' ||
                !(initialId in (options || {}))
                ? null
                : {
                      id: initialId,
                      type:
                          options?.[initialId]?.type ||
                          (accountType === 'childcareCentre'
                              ? 'childcareCentre'
                              : 'school'), //as 'school' | 'childcareCentre',
                  }
        }
    )

export const selectInitialOrgId = (state: RootState) => {
    if (state.user.user.loading) return undefined
    const accountType = state.user.user.data?.accountType || null
    switch (accountType) {
        case 'school':
            return (
                state.user.user.data?.schoolUidWeb ||
                state.user.user.data?.schoolUid ||
                Object.keys(state.user.user.data?.schools || {}).at(0)
            )
        case 'childcareCentre':
            return (
                state.user.user.data?.centreUid ||
                Object.keys(state.user.user.data?.centres || {}).at(0)
            )
        case 'organisation': {
            return (
                state.user.user.data?.schoolUidWeb ||
                state.user.user.data?.schoolUid ||
                state.user.user.data?.centreUid ||
                Object.keys(state.user.user.data?.schools || {}).at(0) ||
                Object.keys(state.user.user.data?.centres || {}).at(0)
            )
        }
        default:
            return null
    }
}

export default userSlice.reducer
